import { AppRegistry } from 'react-native';
import App from './App';
import * as serviceWorkerRegistration from './src/serviceWorkerRegistration';
import { startUpdateChecks } from './src/services/updateChecker';

// Set build info globally BEFORE anything else
// FAIL LOUDLY if build values are missing
if (typeof __BUILD_VERSION__ === 'undefined') {
  const error = "❌ FATAL: __BUILD_VERSION__ not defined by webpack. Check webpack.config.js DefinePlugin configuration.";
  console.error(error);
  throw new Error(error);
}
if (typeof __BUILD_TIMESTAMP__ === 'undefined') {
  const error = "❌ FATAL: __BUILD_TIMESTAMP__ not defined by webpack. Check webpack.config.js DefinePlugin configuration.";
  console.error(error);
  throw new Error(error);
}

window.__BUILD_INFO__ = {
  version: __BUILD_VERSION__,
  timestamp: __BUILD_TIMESTAMP__,
  date: typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : new Date().toLocaleString(),
  mode: typeof __BUILD_MODE__ !== 'undefined' ? __BUILD_MODE__ : 'unknown',
};


// Global error handler - logs errors that crash the app
window.addEventListener('error', (event) => {
  console.error('❌ GLOBAL ERROR:', event.error);
  console.error('Message:', event.message);
  console.error('Filename:', event.filename);
  console.error('Line:', event.lineno, 'Column:', event.colno);
  
  // Display error on screen for mobile debugging
  const errorDiv = document.getElementById('error-details');
  if (errorDiv) {
    errorDiv.textContent = `ERROR: ${event.error?.message || event.message}\n\nFile: ${event.filename}\nLine: ${event.lineno}\n\n${event.error?.stack || 'No stack'}`;
    document.getElementById('error-display').classList.add('show');
  }
});

// Catch unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('❌ UNHANDLED PROMISE REJECTION:', event.reason);
  const errorDiv = document.getElementById('error-details');
  if (errorDiv) {
    errorDiv.textContent = `PROMISE REJECTION: ${event.reason?.message || event.reason}\n\n${event.reason?.stack || ''}`;
    document.getElementById('error-display').classList.add('show');
  }
});


try {
  AppRegistry.registerComponent('Pix', () => App);
  AppRegistry.runApplication('Pix', {
    rootTag: document.getElementById('root'),
  });
} catch (error) {
  console.error('❌ FATAL ERROR during app initialization:', error);
  const errorDiv = document.getElementById('error-details');
  if (errorDiv) {
    errorDiv.textContent = `INITIALIZATION ERROR: ${error.message}\n\n${error.stack}`;
    document.getElementById('error-display').classList.add('show');
  }
}

// Build info is set globally for reference
// Version checking and forced updates are handled by checkAndClearCache() in App.js
// This ensures proper cache clearing and page reload when version changes

// Register service worker for offline functionality (production only)
if (process.env.NODE_ENV === 'production') {
  // IMPORTANT: Don't aggressively unregister service workers on mobile
  // This can interfere with Firebase Auth persistence
  // Only register if no service worker exists, or update if needed
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      // If we have existing registrations, just update them
      if (registrations.length > 0) {
        // Service worker already exists, just register to update if needed
        serviceWorkerRegistration.register({
          onSuccess: () => {
            console.log('✅ Service worker already active');
          },
          onUpdate: (registration) => {
            // Tell the waiting worker to take over. The reload is NOT done here
            // -- serviceWorkerRegistration.js reloads on 'controllerchange', once
            // the new worker has actually claimed the page. The old fixed 500ms
            // timer raced activation and lost on slow installs, reloading back
            // onto the old worker and wedging the device on a stale build.
            if (registration && registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          },
        });
      } else {
        // No service worker exists, register fresh
        serviceWorkerRegistration.register({
          onSuccess: () => {
            console.log('✅ Service worker registered');
          },
          onUpdate: (registration) => {
            // Tell the waiting worker to take over. The reload is NOT done here
            // -- serviceWorkerRegistration.js reloads on 'controllerchange', once
            // the new worker has actually claimed the page. The old fixed 500ms
            // timer raced activation and lost on slow installs, reloading back
            // onto the old worker and wedging the device on a stale build.
            if (registration && registration.waiting) {
              registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
          },
        });
      }
    });
  }

  // Ask the server whether a newer build is deployed, and if so pull it in and
  // reload -- silently. Runs on every load/reload, on tab focus, and on a
  // timer. See src/services/updateChecker.js for why this is separate from the
  // service worker's own update() polling.
  startUpdateChecks();
}

// Offline/Online indicator
window.addEventListener('online', () => {
  const indicator = document.getElementById('offline-indicator');
  if (indicator) {
    indicator.classList.remove('show');
  }
});

window.addEventListener('offline', () => {
  const indicator = document.getElementById('offline-indicator');
  if (indicator) {
    indicator.classList.add('show');
  }
});

